/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { BreadcrumbArea } from "components/common/Link";
import firebase from "firebase/app";
import { useI18n } from "hooks/i18n/i18n";
import {
  useLinkPhoneNumber,
  LinkPhoneNumberError,
} from "hooks/useLinkPhoneNumber";
import { useState } from "react";
import { formatPhoneNumber, parseMobilePhoneNumber } from "utility/phoneNumber";

import {
  ContentsLayout,
  EditForm,
  LinkButton,
  myPageEditMixin,
} from "../styles";

import "firebase/auth";

import type { FC } from "react";

const RECAPTCHA_CONTAINER_ID = "phone-number-registration-recaptcha";

// EditForm はラベルを p で描く作りのため、accessible name は aria-labelledby で与える
const PHONE_NUMBER_LABEL_ID = "phone-number-registration-phone-number-label";
const VERIFICATION_CODE_LABEL_ID = "phone-number-registration-code-label";

// LinkButton は styled.p なので、見た目を保ったまま button として描画する。
// p のままだと Enter での送信も disabled も効かない。
const FormButton = LinkButton.withComponent("button");

const ERROR_MESSAGE_KEY = {
  invalidPhoneNumber: "phoneNumber.errorInvalidPhoneNumber",
  invalidCode: "phoneNumber.errorInvalidCode",
  codeExpired: "phoneNumber.errorCodeExpired",
  alreadyInUse: "phoneNumber.errorAlreadyInUse",
  alreadyLinked: "phoneNumber.errorAlreadyLinked",
  tooManyRequests: "phoneNumber.errorTooManyRequests",
  recaptchaFailed: "phoneNumber.errorRecaptchaFailed",
  unavailable: "phoneNumber.errorUnavailable",
  unknown: "phoneNumber.errorUnknown",
} as const satisfies Record<LinkPhoneNumberError, string>;

/**
 * 現地チケット購入に必要な電話番号をアカウントへ紐付けるページ。
 * 既にメールアドレスでログインしているアカウントに対してSMS認証で紐付ける。
 */
export const PhoneNumberRegistration: FC = () => {
  const { t } = useI18n();
  const classes = styles();

  const {
    state,
    error,
    isSubmitting,
    requestVerificationCode,
    confirmVerificationCode,
    backToInput,
  } = useLinkPhoneNumber(RECAPTCHA_CONTAINER_ID);

  const [phoneNumberInput, setPhoneNumberInput] = useState("");
  const [codeInput, setCodeInput] = useState("");

  // 認証直後は firebase.User が同一参照のまま更新されるため、storeではなく currentUser を見る
  const registeredPhoneNumber =
    firebase.auth().currentUser?.phoneNumber ?? null;
  const parsedRegisteredPhoneNumber =
    registeredPhoneNumber === null
      ? null
      : parseMobilePhoneNumber(registeredPhoneNumber);

  const isSendDisabled = isSubmitting || phoneNumberInput === "";
  const isVerifyDisabled = isSubmitting || codeInput === "";

  return (
    <div id="contents" css={classes.container}>
      <BreadcrumbArea
        paths={[
          // BreadcrumbArea が "ホーム" を common.routes.home へ差し替える
          // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
          ["/", "ホーム"],
          ["/account", t("common.routes.account")],
          ["", t("phoneNumber.header")],
        ]}
      />
      <ContentsLayout>
        {state.step === "completed" || registeredPhoneNumber !== null ? (
          <div css={classes.block}>
            {state.step === "completed" && (
              <p css={classes.description}>{t("phoneNumber.completed")}</p>
            )}
            <EditForm>
              <p>{t("phoneNumber.registered")}</p>
              <p css={classes.phoneNumber}>
                {parsedRegisteredPhoneNumber === null
                  ? (registeredPhoneNumber ?? "")
                  : formatPhoneNumber(parsedRegisteredPhoneNumber)}
              </p>
            </EditForm>
          </div>
        ) : (
          <div>
            <div css={classes.block}>
              <p css={classes.description}>{t("phoneNumber.description")}</p>
              <p css={classes.annotation}>
                {t("phoneNumber.annotationOnePerAccount")}
              </p>
              <p css={classes.annotation}>{t("phoneNumber.annotationSms")}</p>
              <p css={classes.annotation}>
                {t("phoneNumber.annotationInternational")}
              </p>
            </div>

            {error !== null && (
              <p css={[classes.block, classes.error]}>
                {t(ERROR_MESSAGE_KEY[error])}
              </p>
            )}

            {state.step === "input" ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (isSendDisabled) return;
                  void requestVerificationCode(phoneNumberInput);
                }}
              >
                <div css={classes.form}>
                  <EditForm>
                    <p id={PHONE_NUMBER_LABEL_ID}>
                      {t("phoneNumber.inputLabel")}
                    </p>
                    <input
                      aria-labelledby={PHONE_NUMBER_LABEL_ID}
                      type="text"
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder={t("phoneNumber.inputPlaceholder")}
                      value={phoneNumberInput}
                      onChange={(e) =>
                        setPhoneNumberInput(e.currentTarget.value)
                      }
                    />
                  </EditForm>
                </div>
                <FormButton
                  type="submit"
                  disabled={isSendDisabled}
                  css={[classes.block, classes.formButton]}
                >
                  {t("phoneNumber.sendCode")}
                </FormButton>
              </form>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (isVerifyDisabled) return;
                  void confirmVerificationCode(codeInput);
                }}
              >
                <div css={classes.form}>
                  <p css={classes.description}>{t("phoneNumber.codeSent")}</p>
                  {/* この番号はまだ登録されていないため、登録済みのラベルは付けない */}
                  <EditForm>
                    <p css={classes.phoneNumber}>
                      {formatPhoneNumber(state.sentPhoneNumber)}
                    </p>
                  </EditForm>
                  <EditForm>
                    <p id={VERIFICATION_CODE_LABEL_ID}>
                      {t("phoneNumber.codeInputLabel")}
                    </p>
                    <input
                      aria-labelledby={VERIFICATION_CODE_LABEL_ID}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={codeInput}
                      onChange={(e) => setCodeInput(e.currentTarget.value)}
                    />
                  </EditForm>
                </div>
                <FormButton
                  type="submit"
                  disabled={isVerifyDisabled}
                  css={[classes.block, classes.formButton]}
                >
                  {t("phoneNumber.verify")}
                </FormButton>
                <FormButton
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => {
                    setCodeInput("");
                    backToInput();
                  }}
                  css={[classes.block, classes.formButton, classes.backButton]}
                >
                  {t("phoneNumber.resend")}
                </FormButton>
              </form>
            )}
          </div>
        )}

        {/* invisible reCAPTCHA の描画先。Phone provider が必須とするため常に置いておく */}
        <div id={RECAPTCHA_CONTAINER_ID} />
      </ContentsLayout>
    </div>
  );
};

const styles = () => {
  // マイページ下層と同じ、本文の横幅を揃えるための共通指定
  const block = css({
    width: "100%",
    margin: "0 auto",
    "@media screen and (min-width: 768px)": {
      width: "740px",
    },
  });

  return {
    container: css(myPageEditMixin, {
      "@media screen and (min-width: 768px)": {
        margin: "0 auto",
      },
    }),
    block,
    form: css(block, {
      marginBottom: "40px",
    }),
    description: css({
      marginBottom: "25px",
      fontSize: "14px",
      lineHeight: "1.5em",
    }),
    annotation: css({
      marginTop: "8px",
      color: "#5f6667",
      fontSize: "12px",
      lineHeight: "1.5em",
    }),
    error: css({
      marginBottom: "25px",
      color: "#ff0000",
      fontSize: "14px",
      fontWeight: "bold",
    }),
    phoneNumber: css({
      fontSize: "18px",
      fontWeight: "bold",
    }),
    // button は font を継承しないので、p のときと同じ見た目に揃える
    formButton: css({
      fontFamily: "inherit",
      "&:disabled": {
        opacity: 0.4,
        cursor: "default",
      },
    }),
    backButton: css({
      marginTop: "16px",
      color: "#5f6667",
      backgroundColor: "#fff",
      border: "2px solid #5f6667",
      "&:hover": {
        color: "#5f6667",
      },
    }),
  };
};
